import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pen, X } from 'lucide-react';
import { z } from 'zod';
import { PatternFormat } from 'react-number-format';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { getApiErrorMessage } from '@/lib/utils';
import { employeesApi } from '../api/clients.api';

interface EmployeeActionDialogProps {
  id?: number;
  clientId: number;
  onSuccess?: () => void;
}

export function EmployeeActionDialog({ id, clientId, onSuccess }: EmployeeActionDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const employeeSchema = z.object({
    name: z.string().min(1, t('validation.nameRequired')),
    phone: z
      .string()
      .regex(/^\+998 \(\d{2}\) \d{3} \d{2} \d{2}$/, t('validation.phoneInvalid')),
    email: z.string().email(t('validation.emailInvalid')).or(z.literal('')).optional(),
    role: z.string().optional(),
    clientId: z.number(),
  });

  type EmployeeFormData = z.infer<typeof employeeSchema>;

  const form = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      role: '',
      clientId: clientId,
    },
  });

  const { isSubmitting } = form.formState;

  // Load employee data for editing
  const { mutate: loadEmployee } = useMutation({
    mutationFn: (employeeId: number) => employeesApi.getById(employeeId),
    onSuccess: (data) => {
      if (data) {
        const rawPhone = data.phone?.replace(/\D/g, '');
        const formattedPhone =
          rawPhone && rawPhone.length === 12
            ? `+${rawPhone.slice(0, 3)} (${rawPhone.slice(3, 5)}) ${rawPhone.slice(5, 8)} ${rawPhone.slice(8, 10)} ${rawPhone.slice(10, 12)}`
            : '';
        form.reset({
          name: data.name || '',
          phone: formattedPhone || '',
          email: data.email || '',
          role: data.role || data.position || '',
          clientId: clientId,
        });
      }
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('toast.errorLoading', { message: 'Unknown error' })));
    },
  });

  // Create mutation
  const { mutate: createEmployee } = useMutation({
    mutationFn: employeesApi.create,
    onSuccess: () => {
      toast.success(t('toast.createdSuccess', { item: t('employee') }));
      queryClient.invalidateQueries({ queryKey: ['employees', clientId] });
      form.reset({ name: '', phone: '', email: '', role: '', clientId });
      setOpen(false);
      onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('toast.errorCreating', { item: t('employee') })));
    },
  });

  // Update mutation
  const { mutate: updateEmployee } = useMutation({
    mutationFn: ({ employeeId, data }: { employeeId: number; data: Partial<EmployeeFormData> }) =>
      employeesApi.update(employeeId, data),
    onSuccess: () => {
      toast.success(t('toast.updatedSuccess', { item: t('employee') }));
      queryClient.invalidateQueries({ queryKey: ['employees', clientId] });
      setOpen(false);
      onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('toast.errorUpdating', { item: t('employee') })));
    },
  });

  useEffect(() => {
    if (open && id) {
      loadEmployee(id);
    }
    if (open && !id) {
      form.reset({ name: '', phone: '', email: '', role: '', clientId });
    }
  }, [open, id, clientId]);

  const onSubmit = (values: EmployeeFormData, action: 'create' | 'update' | 'createAndContinue') => {
    const payload = {
      ...values,
      phone: values.phone ? values.phone.replace(/\D/g, '') : '',
    };

    if (action === 'update' && id) {
      updateEmployee({ employeeId: id, data: payload });
    } else {
      createEmployee(payload);
      if (action === 'createAndContinue') {
        form.reset({ name: '', phone: '', email: '', role: '', clientId });
      }
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {id ? (
          <Button size="sm" variant="outline" className="rounded-r-none">
            <Pen className="h-4 w-4" />
          </Button>
        ) : (
          <Button>
            {t('new')} <span className="lowercase">{t('employee')}</span>
          </Button>
        )}
      </AlertDialogTrigger>

      <AlertDialogContent className="p-6 max-w-lg">
        <div className="flex items-center justify-between border-b pb-4 border-[#D9D9D9]">
          <AlertDialogTitle className="text-xl font-semibold">
            {id ? t('actions.edit') : t('new')} <span className="lowercase">{t('employee')}</span>
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <button onClick={() => setOpen(false)} aria-label={t('close')}>
              <X size={24} />
            </button>
          </AlertDialogDescription>
        </div>

        <Form {...form}>
          <form className="mt-4 flex flex-col gap-4" noValidate>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('fullName')} <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t('enterFullName')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('email')}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t('enterEmail')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('role')}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t('enterRole')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('phoneNumber')} <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <PatternFormat
                      {...field}
                      format="+998 (##) ### ## ##"
                      allowEmptyFormatting
                      mask="_"
                      placeholder={t('enterPhone')}
                      className={`w-full rounded-md border px-3 py-2 text-sm ${
                        form.formState.errors.phone ? 'border-red-500' : 'border-input'
                      }`}
                      onValueChange={(values) => field.onChange(values.formattedValue)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <AlertDialogFooter className="flex justify-end gap-2 mt-4">
              <AlertDialogCancel onClick={() => setOpen(false)}>{t('cancel')}</AlertDialogCancel>

              {id ? (
                <Button
                  type="button"
                  disabled={isSubmitting}
                  onClick={form.handleSubmit((values) => onSubmit(values, 'update'))}
                >
                  {isSubmitting ? t('saving') : t('actions.save')}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isSubmitting}
                    onClick={form.handleSubmit((values) => onSubmit(values, 'createAndContinue'))}
                  >
                    {isSubmitting ? t('saving') : t('saveAndCreate')}
                  </Button>
                  <Button
                    type="button"
                    disabled={isSubmitting}
                    onClick={form.handleSubmit((values) => onSubmit(values, 'create'))}
                  >
                    {isSubmitting ? t('saving') : t('actions.save')}
                  </Button>
                </>
              )}
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
